import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiDeleteFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  ActionDetailResponse,
  ActionIdVars,
  ActionSummaryResponse,
  AutoScheduleResponse,
  AutoScheduleVars,
  DecomposeStrategicResponse,
  ExecLogBulkResponse,
  ExecLogBulkVars,
  ListActionsResponse,
  ListActionsVars,
  MediaActionApiPath,
  UpdateActionResponse,
  UpdateActionVars
} from '@/common/api/params/mediaAction'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import {
  ACTION_STATUS_FILTERS,
  ActionTabKey,
  MEDIA_ACTION_VIEW_TABS,
  MediaActionViewTabKey
} from '@/common/app/modules/media/mediaAction'

// -----------------------------------------------
// 検索条件
// -----------------------------------------------

const VALID_MEDIA_ACTION_VIEWS = MEDIA_ACTION_VIEW_TABS.map((t) => t.key) as string[]
const VALID_ACTION_STATUS_FILTERS = ACTION_STATUS_FILTERS.map((t) => t.key) as string[]

export interface MediaActionSearchCondition extends SearchConditionBase {
  view: MediaActionViewTabKey | null
  status: ActionTabKey | null
  actionType: string | null
  sourceType: string | null
  sourceAnalysisId: number | null
  sort: string | null
  q: string | null
}

const mediaActionSearchConditionParam: SearchConditionParam[] = [
  { name: 'view', type: 'string' },
  { name: 'status', type: 'string' },
  { name: 'actionType', type: 'string' },
  { name: 'sourceType', type: 'string' },
  { name: 'sourceAnalysisId', type: 'number' },
  { name: 'sort', type: 'string' },
  { name: 'q', type: 'string' }
]

export const useMediaActionSearchCondition = (): [
  MediaActionSearchCondition,
  Dispatch<SetStateAction<MediaActionSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<MediaActionSearchCondition>(
    mediaActionSearchConditionParam,
    (paramValues) => ({
      ...paramValues,
      view:
        paramValues.view && VALID_MEDIA_ACTION_VIEWS.includes(paramValues.view)
          ? paramValues.view
          : null,
      status:
        paramValues.status && VALID_ACTION_STATUS_FILTERS.includes(paramValues.status)
          ? paramValues.status
          : null
    })
  )
}

// 一覧／ロードマップで取得条件が異なるため、view に応じて vars を組み立てる
export const convertConditionToMediaActionListVars = (
  siteId: number,
  condition: MediaActionSearchCondition
): ListActionsVars => {
  const status = condition.status ?? 'suggested'

  return condition.view === 'roadmap'
    ? {
        site_id: siteId,
        sort: 'scheduled_date',
        action_type: condition.actionType || null,
        source_type: condition.sourceType || null,
        source_analytics_id: condition.sourceAnalysisId || null,
        q: condition.q || null
      }
    : {
        site_id: siteId,
        status,
        sort: condition.sort ?? 'priority',
        unscheduled_only: status === 'suggested' ? true : undefined,
        action_type: condition.actionType || null,
        source_type: condition.sourceType || null,
        source_analytics_id: condition.sourceAnalysisId || null,
        q: condition.q || null,
        page: condition.page,
        per_page: condition.perPage
      }
}

// -----------------------------------------------
// GET hooks
// -----------------------------------------------

export const useMediaActionsListApi = (vars: ListActionsVars | null) => {
  return useSWR<ListActionsResponse, Response, [string, ListActionsVars] | null>(
    vars ? [MediaActionApiPath.LIST_ACTIONS, vars] : null,
    apiDefaultFetcher
  )
}

export const useMediaActionSummaryApi = (vars: { site_id: number }) => {
  return useSWR<ActionSummaryResponse, Response, [string, { site_id: number }]>(
    [MediaActionApiPath.GET_SUMMARY, vars],
    apiDefaultFetcher
  )
}

export const useMediaActionDetailApi = (vars: ActionIdVars | null) => {
  return useSWR<ActionDetailResponse, Response, [string, ActionIdVars] | null>(
    vars ? [MediaActionApiPath.GET_ACTION_DETAIL, vars] : null,
    apiDefaultFetcher
  )
}

// 実行ログ（autoRefresh=true で5秒ポーリング）
export const useMediaActionExecLogBulkApi = (vars: ExecLogBulkVars | null, autoRefresh = false) => {
  return useSWR<ExecLogBulkResponse, Response, [string, ExecLogBulkVars] | null>(
    vars && vars.ids ? [MediaActionApiPath.GET_EXEC_LOG_BULK, vars] : null,
    apiDefaultFetcher,
    { refreshInterval: autoRefresh ? 5000 : 0 }
  )
}

// -----------------------------------------------
// Mutation hooks
// -----------------------------------------------

export const useUpdateMediaActionMutation = (options?: MutationOptions<UpdateActionResponse>) => {
  return useCallMutation<UpdateActionResponse, UpdateActionVars>(
    MediaActionApiPath.UPDATE_ACTION,
    options,
    apiPutFetcher
  )
}

export const useMediaActionAutoScheduleMutation = (
  options?: MutationOptions<AutoScheduleResponse>
) => {
  return useCallMutation<AutoScheduleResponse, AutoScheduleVars>(
    MediaActionApiPath.AUTO_SCHEDULE,
    options
  )
}

export const useRequestAutoArticleCreationMutation = (
  options?: MutationOptions<UpdateActionResponse>
) => {
  return useCallMutation<UpdateActionResponse, ActionIdVars>(
    MediaActionApiPath.REQUEST_AUTO_ARTICLE_CREATION,
    options
  )
}

// 戦略アクションをAIで具体アクションに分解する（運営者の手動操作のみ）
export const useDecomposeMediaActionStrategicMutation = (
  options?: MutationOptions<DecomposeStrategicResponse>
) => {
  return useCallMutation<DecomposeStrategicResponse, ActionIdVars>(
    MediaActionApiPath.DECOMPOSE_STRATEGIC,
    options
  )
}

export const useDeleteMediaActionMutation = (options?: MutationOptions<Record<string, never>>) => {
  return useCallMutation<Record<string, never>, ActionIdVars>(
    MediaActionApiPath.UPDATE_ACTION,
    options,
    apiDeleteFetcher
  )
}
